import { useCallback, useMemo } from 'react';
import {
  SW_HEIGHT_CLAMP_DEFAULTS,
  SwHeightClamps,
  SwHeightEquationType,
  SwHeightMethod,
  SwHeightParameterPair,
  SwHeightRockTypeParams,
  SwHeightSettings
} from '../../@types/swHeight/types';
import { ParameterKey, numberOrNull } from './swHeightSettings';

export interface SwHeightPatchApi {
  settings: SwHeightSettings;
  method: SwHeightMethod;
  clamps: SwHeightClamps;
  overrides: Record<string, SwHeightRockTypeParams>;
  patch: (changes: Partial<SwHeightSettings>) => void;
  patchClamp: (key: keyof SwHeightClamps, raw: string) => void;
  patchParameter: (
    key: ParameterKey,
    changes: Partial<SwHeightParameterPair>
  ) => void;
  patchOverrides: (next: Record<string, SwHeightRockTypeParams>) => void;
  resetClamps: () => void;
  resetParameter: (
    key: ParameterKey,
    fallback: SwHeightParameterPair | null
  ) => void;
}

/**
 * The single place Sw-height settings edits are merged.
 *
 * The controls are spread across the rail and three drawers, but they all
 * funnel through one `onChange(settings)` upward — which is what keeps
 * `toComputePayload` complete. Call this **once**, in the page, and hand the
 * result down; no component should merge on its own.
 *
 * Known limitation, inherited rather than introduced: `patch` closes over
 * `settings`, so two edits landing in the same tick both derive from the
 * same base and the second wins. Fixing it would mean making `onChange` a
 * reducer, and that upward contract is precisely what lets the page derive
 * a complete compute payload from one object. Every edit here is a discrete
 * keystroke and the page debounces, so it does not bite in practice.
 */
export const useSwHeightSettingsPatch = (
  settings: SwHeightSettings,
  onChange: (settings: SwHeightSettings) => void
): SwHeightPatchApi => {
  const patch = useCallback(
    (changes: Partial<SwHeightSettings>) =>
      onChange({ ...settings, ...changes }),
    [settings, onChange]
  );

  const clamps = settings.clamps ?? SW_HEIGHT_CLAMP_DEFAULTS;
  const method: SwHeightMethod = settings.method ?? 'brooks_corey';
  const overrides = settings.rock_type_overrides ?? {};

  const patchClamp = useCallback(
    (key: keyof SwHeightClamps, raw: string) => {
      const value = numberOrNull(raw);
      if (value === null) return;
      patch({ clamps: { ...clamps, [key]: value } });
    },
    [patch, clamps]
  );

  const patchParameter = useCallback(
    (key: ParameterKey, changes: Partial<SwHeightParameterPair>) => {
      const current = settings[key] ?? {
        p1: 0,
        p2: 0,
        equation_type: 'linear' as SwHeightEquationType,
        source: 'manual' as const
      };
      patch({
        [key]: { ...current, ...changes, source: 'manual' }
      } as Partial<SwHeightSettings>);
    },
    [settings, patch]
  );

  const patchOverrides = useCallback(
    (next: Record<string, SwHeightRockTypeParams>) =>
      patch({ rock_type_overrides: next }),
    [patch]
  );

  const resetClamps = useCallback(
    () => patch({ clamps: SW_HEIGHT_CLAMP_DEFAULTS }),
    [patch]
  );

  const resetParameter = useCallback(
    (key: ParameterKey, fallback: SwHeightParameterPair | null) =>
      patch({ [key]: fallback } as Partial<SwHeightSettings>),
    [patch]
  );

  return useMemo(
    () => ({
      settings,
      method,
      clamps,
      overrides,
      patch,
      patchClamp,
      patchParameter,
      patchOverrides,
      resetClamps,
      resetParameter
    }),
    [
      settings,
      method,
      clamps,
      overrides,
      patch,
      patchClamp,
      patchParameter,
      patchOverrides,
      resetClamps,
      resetParameter
    ]
  );
};

export default useSwHeightSettingsPatch;
